import { useState, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface HsCodeRule {
  id: string
  customer_id: string | null
  from_hs_code: string
  to_hs_code: string
  reason: string | null
  is_active: boolean
  created_by: string | null
  created_at: string
  updated_at: string
  customer?: { id: string; name: string } | null
}

export interface HsCodeRuleInput {
  customer_id: string | null
  from_hs_code: string
  to_hs_code: string
  reason: string | null
}

function extractErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message
  if (e && typeof e === 'object' && 'message' in e) return String((e as any).message)
  return String(e)
}

export function useHsCodeRules() {
  const [rules, setRules] = useState<HsCodeRule[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRules = useCallback(async (customerId?: string | null) => {
    setLoading(true)
    setError(null)
    try {
      let q = supabase
        .from('hs_code_rules')
        .select('*')
        .order('created_at', { ascending: false })

      if (customerId !== undefined) {
        if (customerId) q = q.eq('customer_id', customerId)
        else q = q.is('customer_id', null)
      }

      const { data, error: err } = await q
      if (err) throw err

      // Fetch customer names for rules that have a customer_id
      const customerIds = [...new Set((data || []).map(r => r.customer_id).filter(Boolean))] as string[]
      let customerMap: Record<string, { id: string; name: string }> = {}
      if (customerIds.length > 0) {
        const { data: customers } = await supabase
          .from('customers')
          .select('id, name')
          .in('id', customerIds)
        customers?.forEach(c => { customerMap[c.id] = c })
      }

      setRules(
        (data || []).map(r => ({
          ...r,
          customer: r.customer_id ? (customerMap[r.customer_id] ?? null) : null,
        })) as HsCodeRule[]
      )
    } catch (e) {
      setError(extractErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchMatchingRules = useCallback(async (hsCodes: string[], customerId?: string | null): Promise<HsCodeRule[]> => {
    if (!hsCodes.length) return []
    try {
      let q = supabase
        .from('hs_code_rules')
        .select('*')
        .in('from_hs_code', hsCodes)
        .eq('is_active', true)

      if (customerId) {
        q = q.or(`customer_id.is.null,customer_id.eq.${customerId}`)
      } else {
        q = q.is('customer_id', null)
      }

      const { data, error: err } = await q
      if (err) throw err

      const customerIds = [...new Set((data || []).map(r => r.customer_id).filter(Boolean))] as string[]
      let customerMap: Record<string, { id: string; name: string }> = {}
      if (customerIds.length > 0) {
        const { data: customers } = await supabase
          .from('customers')
          .select('id, name')
          .in('id', customerIds)
        customers?.forEach(c => { customerMap[c.id] = c })
      }

      return (data || []).map(r => ({
        ...r,
        customer: r.customer_id ? (customerMap[r.customer_id] ?? null) : null,
      })) as HsCodeRule[]
    } catch {
      return []
    }
  }, [])

  const createRule = useCallback(async (input: HsCodeRuleInput): Promise<HsCodeRule> => {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error: err } = await supabase
      .from('hs_code_rules')
      .insert({ ...input, created_by: user?.id ?? null })
      .select('*')
      .single()
    if (err) throw new Error(err.message)
    return {
      ...(data as HsCodeRule),
      customer: null,
    }
  }, [])

  const updateRule = useCallback(async (id: string, patch: Partial<HsCodeRuleInput & { is_active: boolean }>): Promise<void> => {
    const { error: err } = await supabase
      .from('hs_code_rules')
      .update(patch)
      .eq('id', id)
    if (err) throw new Error(err.message)
  }, [])

  const deleteRule = useCallback(async (id: string): Promise<void> => {
    const { error: err } = await supabase.from('hs_code_rules').delete().eq('id', id)
    if (err) throw new Error(err.message)
  }, [])

  return { rules, loading, error, fetchRules, fetchMatchingRules, createRule, updateRule, deleteRule }
}
